import {
  Autocomplete,
  AutocompleteRenderOptionState,
  Chip,
  TextField,
  TextFieldProps,
  createFilterOptions,
} from "@mui/material";
import {
  HTMLAttributes,
  ReactNode,
  memo,
  useCallback,
  useEffect,
  useState,
} from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AxiosResponse } from "axios";
import useFetch from "../../../hooks/useFetch";
import useDebounce from "../../../hooks/useDebounce";
import { IMedicCities } from "../../../models/medic-guide";

type IFormInputProps = {
  name: string;
  options?: IMedicCities[];
  onValueSelected?: (data: IMedicCities) => void;
  request?: (params?: any) => Promise<AxiosResponse<IMedicCities[]>>;
  serviceType?: "List" | "Page";
  params?: any;
  SearchAsYouTypeParams?: (typedValue: string) => any;
  getOptionLabel: ((option: IMedicCities) => string) | undefined;
  isOptionEqualToValue?: (option: IMedicCities, value: IMedicCities) => boolean;
  filterSelectedOptions?: boolean;
  isClearable?: boolean;
  disable?: boolean;
  renderOption?:
    | ((
        props: HTMLAttributes<HTMLLIElement>,
        option: IMedicCities,
        state: AutocompleteRenderOptionState
      ) => ReactNode)
    | undefined;
} & TextFieldProps;

const filterOptions = createFilterOptions({
  matchFrom: "any",
  stringify: (option: IMedicCities) => option.city.name,
});

const AutoComplete = ({
  name,
  options,
  onValueSelected,
  params,
  request,
  getOptionLabel,
  SearchAsYouTypeParams,
  isOptionEqualToValue = (o, v) => {
    return o.id === v.id;
  },
  disable,
  filterSelectedOptions = false,
  isClearable = true,
  renderOption = (props, option) => {
    return (
      <li {...props} key={option.id}>
        {option.city.name}
      </li>
    );
  },
  ...otherProps
}: IFormInputProps) => {
  const { control } = useFormContext();
  const [realOptions, setOptions] = useState<IMedicCities[]>(options || []);
  const { loading, sendRequest } = useFetch(request);
  const { t } = useTranslation();
  const [value, setValue] = useState<string>("");

  const searchQuery: string = useDebounce(value, 500);

  const loadRequest = useCallback(
    async (parameters?: any) => {
      const { data, success } = await sendRequest(parameters);

      if (success) {
        if (searchQuery) {
          setOptions(
            data.filter((d) =>
              d.city.name
                .toLocaleLowerCase()
                .includes(searchQuery.toLocaleLowerCase())
            )
          );
        } else {
          setOptions(data);
        }
      }
    },
    [sendRequest, searchQuery]
  );

  //Debounce get function
  useEffect(() => {
    if (SearchAsYouTypeParams) {
      if (searchQuery !== "" && searchQuery.length > 2) {
        loadRequest(SearchAsYouTypeParams(searchQuery));
      }
    }
  }, [searchQuery, SearchAsYouTypeParams, loadRequest]);

  useEffect(() => {
    if (!SearchAsYouTypeParams) {
      loadRequest(params);
    }
  }, [loadRequest, SearchAsYouTypeParams, params]);

  return (
    <Controller
      render={({ fieldState: { error }, field }) => {
        const { onChange, value, ref } = field;
        return (
          <Autocomplete
            value={value}
            loading={loading}
            disabled={disable ? true : false}
            noOptionsText={t("components.autocomplete.noOptions")}
            renderOption={renderOption}
            isOptionEqualToValue={isOptionEqualToValue}
            filterSelectedOptions={filterSelectedOptions}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={option.city.name}
                  color="primary"
                  size="small"
                  {...getTagProps({ index })}
                />
              ))
            }
            filterOptions={params ? filterOptions : (x) => x}
            options={options ? options : realOptions}
            getOptionLabel={getOptionLabel}
            onChange={(event: any, newValue) => {
              if (!newValue) {
                onChange(null);
              }
              if (newValue) {
                onChange(newValue);
                if (onValueSelected) {
                  onValueSelected(newValue);
                }
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                {...otherProps}
                error={!!error}
                onChange={(e) => setValue(e.target.value)}
                inputRef={ref}
                FormHelperTextProps={{
                  sx: {
                    margin: 0,
                    marginLeft: 1,
                  },
                }}
                helperText={
                  error
                    ? t(error?.message || "")
                    : SearchAsYouTypeParams
                    ? t("components.autocomplete.hint")
                    : ""
                }
                fullWidth
              />
            )}
          />
        );
      }}
      defaultValue={null}
      name={name}
      control={control}
    />
  );
};

export default memo(AutoComplete);
